'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';

/**
 * Workspace "retry book": re-run the whole pipeline (analysis → portraits →
 * cover → pages) on a FAILED illustration. The executor reserves analysis again
 * (mandatory), resets the shell and starts a fresh standalone run.
 */
export async function retryIllustrationGeneration(input: {
  bookId: string;
}): Promise<ActionResult<{ ok: true }>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };

    const { v2BookOf, v2Failure } = await import('@/contexts/pipeline-v2/executor/legacyEntry.server');
    const v2 = await v2BookOf(input.bookId, familyId);
    if (v2) {
      const { getOrderExecutor } = await import('@/contexts/pipeline-v2/executor/server');
      const r = await getOrderExecutor().retryEntity({ storyId: v2.storyId, familyId, kind: 'illustration', entityId: v2.id });
      return r.ok ? { success: true, data: { ok: true } } : v2Failure(r);
    }

    const { getContentPlanExecutor } = await import('@/contexts/content/services/ContentPlanExecutor');
    await getContentPlanExecutor().restartBookGeneration(input.bookId, familyId);
    return { success: true, data: { ok: true } };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('retryIllustrationGeneration', e) };
  }
}
